import React, { useEffect, useState } from "react";
import { loadStyles } from "../../../utils/cssLoader";

const EVENT_EMOJI = {
  Goal: "\u26BD",
  "Own goal": "\u26BD",
  "Disallowed goal": "\u274C",
  Penalty: "\u26BD",
  "Yellow card": "\uD83D\uDFE8",
  "Red card": "\uD83D\uDFE5",
  "Second yellow card": "\uD83D\uDFE8",
  Sub: "\uD83D\uDD04",
};

const TimelineEvent = ({ event }) => {
  const isHome = event?.isH === 1 || event?.isH === true;
  const emoji = EVENT_EMOJI[event?.typ] || "";
  const playerName = event?.pl_nm || event?.pl_in_nm || "";
  const assist = event?.pl_out_nm || event?.asst || "";

  return (
    <div className={`mtl__event mtl__event--${isHome ? "home" : "away"}`}>
      <span className={`mtl__dot mtl__dot--${isHome ? "home" : "away"}`} />
      <div className="mtl__event-card">
        {emoji && <span className="mtl__icon-wrap">{emoji}</span>}
        <div className="mtl__event-info">
          <span className="mtl__player">{playerName}</span>
          {assist && <span className="mtl__assist">{assist}</span>}
        </div>
        <span className="mtl__minute">{event?.mins}''</span>
      </div>
    </div>
  );
};

const MatchTimeline = ({
  data,
  dataClient = false,
  loadCSS = true,
  title,
  matchDetail,
  matchId,
  baseUrl,
  sport = "football",
}) => {
  const [timelineData, setTimelineData] = useState(data?.timeline || []);
  const [fetchedMatchDetail, setFetchedMatchDetail] = useState(null);

  useEffect(() => {
    loadStyles("matchTimeline");
  }, []);

  // Self-fetch mode: fetch both summary (timeline) + detail (teams/score)
  useEffect(() => {
    if (!matchId || !baseUrl || data?.timeline) return;
    Promise.all([
      fetch(`${baseUrl}/${sport}/match/${matchId}/summary`).then((r) =>
        r.ok ? r.json() : null,
      ),
      fetch(`${baseUrl}/${sport}/match/${matchId}/detail`).then((r) =>
        r.ok ? r.json() : null,
      ),
    ])
      .then(([summary, detail]) => {
        if (summary) setTimelineData(summary?.summary?.timeline || []);
        if (detail) setFetchedMatchDetail(detail);
      })
      .catch(() => {});
  }, [matchId, baseUrl, sport, data]);

  useEffect(() => {
    if (!dataClient) return;
    try {
      const raw = document
        .querySelector(".microservice-cont")
        ?.getAttribute("data-listingdata");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      setTimelineData(parsed?.data?.summary?.timeline || []);
    } catch (e) {
      console.error("[MatchTimeline] parse error:", e);
    }
  }, [dataClient]);

  useEffect(() => {
    if (!dataClient && data?.timeline) {
      setTimelineData(data.timeline);
    }
  }, [data, dataClient]);

  const resolvedMatchDetail = matchDetail || fetchedMatchDetail;

  // Key moments only — filter out HT/FT markers and empty events
  const keyEvents = timelineData.filter(
    (e) =>
      e?.typ && e.typ !== "HT" && e.typ !== "FT" && (e.pl_nm || e.pl_in_nm),
  );

  // Derive score from match detail
  const matchData = resolvedMatchDetail?.data || resolvedMatchDetail;
  const homeTeam = matchData?.competitors?.htn || "";
  const awayTeam = matchData?.competitors?.atn || "";
  const score = matchData?.cfs || "";
  const [homeScore, awayScore] = score.split("-");

  // League name
  const leagueName = matchData?.tournament?.nm || "";

  return (
    <div className="mtl">
      {/* Header */}
      <div className="mtl__header">
        <div className="mtl__header-icon">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        </div>
        <div className="mtl__header-text">
          <span className="mtl__header-title">Match Timeline</span>
          {leagueName && (
            <span className="mtl__header-sub">
              {leagueName} &middot; Key Moments
            </span>
          )}
        </div>
      </div>

      {/* Spine + events */}
      <div className="mtl__body">
        <div className="mtl__spine" />
        {keyEvents.length > 0 ? (
          keyEvents.map((event, i) => (
            <TimelineEvent key={`${event.mins}-${i}`} event={event} />
          ))
        ) : (
          <p className="mtl__empty">No events yet</p>
        )}
      </div>

      {/* Footer score */}
      {score && (
        <div className="mtl__footer">
          <span className="mtl__footer-team mtl__footer-team--home">
            <span className="mtl__footer-dot mtl__footer-dot--home" />
            {homeTeam} {homeScore}
          </span>
          <span className="mtl__footer-sep">-</span>
          <span className="mtl__footer-team mtl__footer-team--away">
            {awayScore} {awayTeam}
            <span className="mtl__footer-dot mtl__footer-dot--away" />
          </span>
        </div>
      )}
    </div>
  );
};

export default MatchTimeline;
